// History (admin) — the append-only audit log, browsable forever (ADR-0010 free win).
// Cursor-paged "Load more"; entries are grouped visually by verb color, not filtered.
import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { apiGet } from '../../lib/api';
import { fmtAgo } from '../../lib/dates';
import { showHistory } from '../../state/adminUi';
import { AdminSheet } from './AdminSheet';

interface AuditEntry {
  id: number;
  at: number;
  role: string;
  name: string;
  action: string;
  entityType: string;
  entityId: string;
  details: unknown;
}
interface AuditPage {
  entries: AuditEntry[];
  hasMore: boolean;
  nextBefore: number | null;
}

const VERBS: Record<string, string> = {
  createWeek: 'created week',
  setDefaultWeek: 'set default week',
  addCall: 'added a call',
  editCall: 'edited a call',
  deleteCall: 'deleted a call',
  moveCall: 'moved a call',
  reorderDay: 'reordered a day',
  rescheduleCall: 'rescheduled a call',
  complete: 'closed out a job',
  reopen: 'reopened a job',
  setReturn: 'flagged a follow-up',
  editNote: 'edited a note',
  park: 'put a job on hold',
  unpark: 'took a job off hold',
  setReminder: 'set a reminder',
  clearReminder: 'cleared a reminder',
  kickViewer: 'removed a device',
  import: 'imported v1 history',
  login: 'signed in',
};

const DESTRUCTIVE = new Set(['deleteCall', 'reopen', 'kickViewer']);

function summarize(details: unknown): string {
  if (!details || typeof details !== 'object') return '';
  const d = details as Record<string, unknown>;
  const bits: string[] = [];
  for (const [k, v] of Object.entries(d)) {
    if (v === null || v === undefined) continue;
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    bits.push(`${k}: ${s}`);
  }
  return bits.join(' · ').slice(0, 160);
}

export function HistorySheet() {
  const close = () => (showHistory.value = false);
  const entries = useSignal<AuditEntry[]>([]);
  const hasMore = useSignal(false);
  const nextBefore = useSignal<number | null>(null);
  const state = useSignal<'loading' | 'ready' | 'error'>('loading');

  async function load(before?: number) {
    try {
      const page = await apiGet<AuditPage>(
        `/api/audit?limit=50${before ? `&before=${before}` : ''}`,
      );
      entries.value = before ? [...entries.value, ...page.entries] : page.entries;
      hasMore.value = page.hasMore;
      nextBefore.value = page.nextBefore;
      state.value = 'ready';
    } catch {
      state.value = 'error';
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <AdminSheet
      title="History"
      sub="Every change ever made, newest first. Nothing here can be edited or deleted."
      onClose={close}
      tall
      testId="history-sheet"
    >
      {state.value === 'loading' && <p class="p-6 text-center text-slate-500">Loading…</p>}
      {state.value === 'error' && (
        <div class="p-6 text-center">
          <p class="text-slate-500">Could not load history.</p>
          <button class="btn-ghost mt-2" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}
      {state.value === 'ready' && entries.value.length === 0 && (
        <p class="p-6 text-center text-slate-500">No activity recorded yet.</p>
      )}

      <div class="space-y-1.5" data-testid="history-list">
        {entries.value.map((e) => {
          const detail = summarize(e.details);
          return (
            <div
              key={e.id}
              class="flex items-baseline gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2"
            >
              <span
                class={`mt-1 h-2 w-2 shrink-0 self-start rounded-full ${DESTRUCTIVE.has(e.action) ? 'bg-red-500' : e.role === 'admin' ? 'bg-brand-500' : e.role === 'tech' ? 'bg-emerald-500' : 'bg-slate-300'}`}
                aria-hidden="true"
              />
              <div class="min-w-0 flex-1">
                <p class="text-sm text-navy-900">
                  <span class="font-bold">
                    {e.name ||
                      (e.role === 'admin' ? 'Office' : e.role === 'tech' ? 'A tech' : e.role)}
                  </span>{' '}
                  {VERBS[e.action] ?? e.action}
                  {e.entityId && <span class="text-slate-400"> · {e.entityId}</span>}
                </p>
                {detail && <p class="truncate text-xs text-slate-500">{detail}</p>}
              </div>
              <span class="shrink-0 text-xs font-semibold whitespace-nowrap text-slate-400">
                {fmtAgo(e.at)}
              </span>
            </div>
          );
        })}
      </div>

      {hasMore.value && (
        <button
          class="btn-ghost mt-3 w-full"
          onClick={() => void load(nextBefore.value ?? undefined)}
        >
          Load older entries
        </button>
      )}
    </AdminSheet>
  );
}
